import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
	X,
	Search,
	Check,
	User,
	Zap,
	Loader2,
	ArrowLeft,
	Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useGetServices } from '@/services/queries/service';
import {
	useCreateClubBenefit,
	useUpdateClubBenefit,
} from '@/services/mutations/club';
import { toast } from 'react-toastify';

interface MixerBenefitModalProps {
	isOpen: boolean;
	onClose: () => void;
	clubId: string;
	initialServiceIds: string[];
	benefitToEdit?: any;
}

type Step = 'selection' | 'configuration';

interface BenefitConfig {
	serviceId: string;
	substituteTitle: string;
	renewalTrigger:
		| 'NEVER'
		| 'ALWAYS_ON_USE'
		| 'ALWAYS_ON_SUBSCRIPTION_PAYMENT';
	discount: number;
	amount: number;
	displayOrder: number;
}

export const MixerBenefitModal = ({
	isOpen,
	onClose,
	clubId,
	initialServiceIds = [],
	benefitToEdit,
}: MixerBenefitModalProps) => {
	const { data: services, isLoading } = useGetServices();
	const { mutateAsync: createBenefit, isPending: isCreating } =
		useCreateClubBenefit();
	const { mutateAsync: updateBenefit, isPending: isUpdating } =
		useUpdateClubBenefit();

	const isPending = isCreating || isUpdating;
	const isEditMode = !!benefitToEdit;

	const [step, setStep] = useState<Step>('selection');
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
	const [configs, setConfigs] = useState<Record<string, BenefitConfig>>({});

	// Centralização de valores padrão para evitar "mocks" acidentais
	const DEFAULT_TRIGGER = 'ALWAYS_ON_SUBSCRIPTION_PAYMENT';

	useEffect(() => {
		if (!isOpen) {
			setConfigs({});
			setSelectedServiceIds([]);
			setSearchTerm('');
			return;
		}

		if (benefitToEdit) {
			setStep('configuration');
			const sId = benefitToEdit.service_id || benefitToEdit.service?.id;
			setSelectedServiceIds([sId]);
			setConfigs({
				[sId]: {
					serviceId: sId,
					substituteTitle: benefitToEdit.service_substitute_title,
					renewalTrigger:
						benefitToEdit.renewal_trigger || DEFAULT_TRIGGER,
					discount: benefitToEdit.service_discount_percentage ?? 0,
					amount: benefitToEdit.default_amount ?? 1,
					displayOrder: benefitToEdit.display_order ?? 0,
				},
			});
		} else {
			setStep('selection');
			setSelectedServiceIds([]);
		}
	}, [isOpen, benefitToEdit]);

	const activeServices = useMemo(() => {
		if (!services) return [];
		if (isEditMode) {
			const sId = benefitToEdit.service_id || benefitToEdit.service?.id;
			return services.filter((s) => s.id === sId);
		}
		const newIds = selectedServiceIds;
		return services.filter((s) => newIds.includes(s.id));
	}, [
		services,
		selectedServiceIds,
		initialServiceIds,
		isEditMode,
		benefitToEdit,
	]);

	useEffect(() => {
		if (isEditMode) return;

		const newConfigs = { ...configs };
		let hasChanges = false;

		activeServices.forEach((s) => {
			if (!newConfigs[s.id]) {
				newConfigs[s.id] = {
					serviceId: s.id,
					substituteTitle: s.service_name,
					renewalTrigger: DEFAULT_TRIGGER,
					discount: 0,
					amount: 1,
					displayOrder: 0,
				};
				hasChanges = true;
			}
		});

		if (hasChanges) setConfigs(newConfigs);
	}, [activeServices, isEditMode]);

	if (!isOpen) return null;

	const filteredServices = Array.isArray(services)
		? services.filter(
				(service) =>
					service.service_name
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					service.professional_label
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()),
		  )
		: [];

	const handleToggleSelect = (id: string) => {
		setSelectedServiceIds((prev) =>
			prev.includes(id)
				? prev.filter((itemId) => itemId !== id)
				: [...prev, id],
		);
	};

	const updateConfig = (
		id: string,
		field: keyof BenefitConfig,
		value: any,
	) => {
		setConfigs((prev) => ({
			...prev,
			[id]: { ...prev[id], [field]: value },
		}));
	};

	const handleFinalize = async () => {
		if (!clubId) return;

		try {
			if (isEditMode) {
				const sId =
					benefitToEdit.service_id || benefitToEdit.service?.id;
				const config = configs[sId];

				// PAYLOAD LIMPO: Removendo club_id e service_id conforme erro 400 do back-end
				const payload = {
					service_substitute_title: config.substituteTitle,
					service_discount_percentage: Number(config.discount),
					default_amount: Number(config.amount),
					renewal_trigger: config.renewalTrigger,
					display_order: Number(config.displayOrder),
				};

				await updateBenefit({
					id: benefitToEdit.id,
					body: payload as any,
				});
			} else {
				await Promise.all(
					activeServices.map((s) => {
						const config = configs[s.id];
						const payload: Clubs.CreateBenefit = {
							club_id: clubId,
							service_id: s.id,
							service_substitute_title: config.substituteTitle,
							service_discount_percentage: Number(
								config.discount,
							),
							default_amount: Number(config.amount),
							renewal_trigger: config.renewalTrigger,
							display_order: Number(config.displayOrder),
						};
						return createBenefit(payload);
					}),
				);
				toast.success(
					`${activeServices.length} novos vínculos criados!`,
				);
			}
			onClose();
		} catch (error) {
			console.error(error);
		}
	};

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>

			<div
				className={cn(
					'animate-in zoom-in-95 relative flex w-full max-w-[550px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200',
					step === 'selection'
						? 'h-[90vh] max-h-[850px]'
						: 'max-h-[90vh]',
				)}
			>
				{/* Header */}
				<div className="relative shrink-0 border-b border-stone-50 px-8 py-6">
					<div className="flex items-center gap-4">
						{step === 'configuration' && !isEditMode && (
							<button
								onClick={() => setStep('selection')}
								className="rounded-full p-1 text-stone-400 outline-none transition-colors hover:bg-stone-50 hover:text-stone-900 focus:outline-none"
							>
								<ArrowLeft size={20} />
							</button>
						)}
						<div>
							<h2 className="text-xl font-black tracking-tight text-stone-900">
								{isEditMode
									? 'Editar Vínculo'
									: step === 'selection'
									  ? 'Vincular Benefícios'
									  : 'Configurar Vínculos'}
							</h2>
							<p className="mt-0.5 text-xs font-medium text-stone-400">
								{isEditMode
									? 'Altere os detalhes deste benefício no clube.'
									: `Ajuste os detalhes para ${activeServices.length} item(ns).`}
							</p>
						</div>
					</div>
					<button
						onClick={onClose}
						className="absolute right-8 top-8 text-stone-300 transition-colors hover:text-stone-500"
					>
						<X size={24} />
					</button>
				</div>

				{/* STEP 1: Selection */}
				{step === 'selection' && (
					<>
						<div className="shrink-0 px-8 pt-6">
							<div className="relative">
								<Search
									className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
									size={18}
								/>
								<input
									type="text"
									placeholder="Buscar por nome ou fornecedor..."
									value={searchTerm}
									onChange={(e) =>
										setSearchTerm(e.target.value)
									}
									className="w-full rounded-xl border border-stone-300 bg-white py-3.5 pl-12 pr-4 text-sm font-medium text-stone-700 outline-none focus:border-stone-900"
								/>
							</div>
						</div>

						<div className="flex-1 overflow-y-auto px-8 py-6">
							{isLoading ? (
								<div className="flex h-full items-center justify-center">
									<p className="animate-pulse text-[10px] font-black uppercase tracking-widest text-stone-300">
										Carregando...
									</p>
								</div>
							) : (
								<div className="space-y-3">
									{filteredServices.map((service) => {
										const isSelected =
											selectedServiceIds.includes(
												service.id,
											);

										return (
											<div
												key={service.id}
												onClick={() =>
													handleToggleSelect(
														service.id,
													)
												}
												className={cn(
													'group relative flex cursor-pointer items-center gap-4 rounded-2xl border-solid bg-white p-3.5 transition-all duration-200',
													isSelected
														? 'border border-black'
														: 'border border-stone-100 hover:border-stone-300',
												)}
											>
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2">
														<h3 className="truncate text-sm font-bold text-stone-900">
															{
																service.service_name
															}
														</h3>
														<span className="shrink-0 text-[9px] font-black uppercase text-stone-300">
															{service.type}
														</span>
													</div>
													{(service.professional_label ||
														service.short_description) && (
														<p className="mt-1.5 truncate text-[11px] font-medium text-stone-400">
															{service.professional_label && (
																<span className="font-bold text-stone-500">
																	{
																		service.professional_label
																	}
																</span>
															)}
															{service.professional_label &&
																service.short_description &&
																' • '}
															{
																service.short_description
															}
														</p>
													)}
												</div>
												<div
													className={cn(
														'flex h-6 w-6 items-center justify-center rounded-full',
														isSelected
															? 'bg-black'
															: 'border border-stone-200',
													)}
												>
													{isSelected && (
														<Check
															size={14}
															className="stroke-[4] text-white"
														/>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</>
				)}

				{/* STEP 2: Configuration */}
				{step === 'configuration' && (
					<div className="flex-1 space-y-10 overflow-y-auto px-8 py-8">
						{activeServices.map((s, index) => {
							const config = configs[s.id] || {
								substituteTitle: s.service_name,
								renewalTrigger: DEFAULT_TRIGGER,
								discount: 0,
								amount: 1,
								displayOrder: 0,
							};

							return (
								<div key={s.id}>
									{index !== 0 && (
										<div className="mb-10 h-px w-full bg-stone-200" />
									)}
									<div className="space-y-6">
										<div className="flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-900 text-white">
												{s.type === 'PROFESSION' ? (
													<User
														size={18}
														strokeWidth={2.5}
													/>
												) : (
													<Zap
														size={18}
														strokeWidth={2.5}
													/>
												)}
											</div>
											<h3 className="text-base font-black tracking-tight text-stone-900">
												{s.service_name}
											</h3>
										</div>

										<div className="space-y-2">
											<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400">
												Prioridade
											</label>
											<input
												type="number"
												min={0}
												value={config.displayOrder}
												onChange={(e) =>
													updateConfig(
														s.id,
														'displayOrder',
														Number(
															e.target.value || 0,
														),
													)
												}
												className="w-full rounded-xl border border-stone-200 bg-stone-50/50 px-5 py-3.5 text-sm font-bold text-stone-600 outline-none focus:border-stone-400 focus:bg-white"
											/>
										</div>

										<div className="space-y-2">
											<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400">
												Título Substituto
											</label>
											<input
												type="text"
												value={config.substituteTitle}
												onChange={(e) =>
													updateConfig(
														s.id,
														'substituteTitle',
														e.target.value,
													)
												}
												className="w-full rounded-xl border border-stone-200 bg-stone-50/50 px-5 py-3.5 text-sm font-bold text-stone-600 outline-none focus:border-stone-400 focus:bg-white"
												placeholder={s.service_name}
											/>
										</div>

										<div className="space-y-3">
											<div className="ml-1 flex items-center gap-1.5">
												<label className="text-[10px] font-black uppercase tracking-widest text-stone-400">
													Gatilho de Renovação
												</label>
												<Info
													size={12}
													className="text-stone-300"
												/>
											</div>
											<div className="flex flex-col gap-2.5">
												{[
													{
														id: 'NEVER',
														label: 'Nunca (NEVER)',
														desc: 'O benefício não renova automaticamente.',
													},
													{
														id: 'ALWAYS_ON_USE',
														label: 'Sempre ao usar (ALWAYS_ON_USE)',
														desc: 'Renova a cada utilização do membro.',
													},
													{
														id: 'ALWAYS_ON_SUBSCRIPTION_PAYMENT',
														label: 'No Pagamento (ALWAYS_ON_SUBSCRIPTION_PAYMENT)',
														desc: 'Renova junto com o ciclo de assinatura.',
													},
												].map((opt) => (
													<button
														key={opt.id}
														type="button"
														onClick={() =>
															updateConfig(
																s.id,
																'renewalTrigger',
																opt.id as any,
															)
														}
														className={cn(
															'w-full rounded-2xl border border-solid p-4 text-left transition-all',
															config.renewalTrigger ===
																opt.id
																? 'border-black bg-stone-50/50'
																: 'border-stone-100 bg-white hover:border-stone-200',
														)}
													>
														<p className="text-xs font-black text-stone-900">
															{opt.label}
														</p>
														<p className="mt-0.5 text-[10px] font-medium text-stone-400">
															{opt.desc}
														</p>
													</button>
												))}
											</div>
										</div>

										<div className="grid grid-cols-2 gap-4">
											<div className="space-y-2">
												<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400">
													Desconto (%)
												</label>
												<input
													type="number"
													value={config.discount}
													onChange={(e) =>
														updateConfig(
															s.id,
															'discount',
															e.target.value,
														)
													}
													className="w-full rounded-xl border border-stone-200 bg-stone-50/50 px-5 py-3.5 text-sm font-bold text-stone-600 outline-none focus:border-stone-400 focus:bg-white"
												/>
											</div>
											<div className="space-y-2">
												<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400">
													Qtd de Usos
												</label>
												<input
													type="number"
													value={config.amount}
													onChange={(e) =>
														updateConfig(
															s.id,
															'amount',
															e.target.value,
														)
													}
													className="w-full rounded-xl border border-stone-200 bg-stone-50/50 px-5 py-3.5 text-sm font-bold text-stone-600 outline-none focus:border-stone-400 focus:bg-white"
												/>
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}

				{/* Footer */}
				<div className="shrink-0 border-t border-stone-50 px-6 py-4 sm:px-8 sm:py-6">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-center text-sm font-bold text-stone-500">
							{!isEditMode && step === 'selection'
								? `${selectedServiceIds.length} selecionados`
								: `${activeServices.length} novos vínculos`}
						</p>
						<div className="flex items-center justify-between gap-4 sm:gap-6">
							<button
								onClick={onClose}
								className="text-sm font-bold text-stone-400 hover:text-stone-600"
							>
								Cancelar
							</button>
							<Button
								text={
									isEditMode
										? isPending
											? 'Salvando...'
											: 'Salvar Alterações'
										: step === 'selection'
										  ? 'Configurar Vínculos'
										  : isPending
										    ? 'Finalizando...'
										    : 'Finalizar Vínculo'
								}
								disabled={
									step === 'selection'
										? activeServices.length === 0
										: isPending
								}
								className={cn(
									'h-auto rounded-full px-6 py-3 text-sm font-bold shadow-lg sm:px-8',
									(
										step === 'selection'
											? activeServices.length > 0
											: !isPending
									)
										? 'bg-black text-white hover:bg-stone-800'
										: 'cursor-not-allowed bg-stone-200 text-stone-400',
								)}
								startIcon={
									isPending && (
										<Loader2 className="mr-1 size-4 animate-spin" />
									)
								}
								onClick={
									step === 'selection'
										? () => setStep('configuration')
										: handleFinalize
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};
